import React, { FC, HTMLAttributes, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import './FormInputNumber.scss';

interface FormInputNumberProps extends HTMLAttributes<HTMLInputElement> {
  step?: number;
  value: number;
  minValue?: number;
  maxValue?: number;
  btnsHandler: (value: number) => void;
}

const FormInputNumber: FC<FormInputNumberProps> = ({
  value,
  onChange,
  minValue = 1,
  maxValue = 9999,
  step = 1,
  placeholder = '',
  btnsHandler,
}) => {
  const [month, setMonth] = useState(value);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isCounterProcess, setIsCounterProcess] = useState(false);

  useEffect(() => {
    if (month) {
      btnsHandler(month);
    }
  }, [btnsHandler, month]);

  useEffect(() => {
    if (isCounterProcess) return;
    setMonth(value);
  }, [value, isCounterProcess]);

  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const inputEvent = e.nativeEvent as InputEvent;
    const target = e.target as HTMLInputElement;
    const { value: newValue, selectionStart, selectionEnd } = target;
    const inserted = inputEvent.data || '';

    const nextValue = newValue.slice(0, selectionStart || 0) + inserted + newValue.slice(selectionEnd ?? 0);

    const numeric = Number(nextValue);

    if (!/^\d+$/.test(nextValue) || numeric < minValue || numeric > maxValue) {
      e.preventDefault();
    }
  };

  const startPressPlus = () => {
    setMonth((prev) => {
      if (!prev) return 2;
      return Math.min(prev + step, maxValue);
    });
    timeoutRef.current = setTimeout(() => {
      setIsCounterProcess(true);
      intervalRef.current = setInterval(() => {
        setMonth((prev) => {
          if (!prev) return 2;
          return Math.min(prev + step, maxValue);
        });
      }, 100);
    }, 200);
  };

  const startPressMinus = () => {
    setMonth((prev) => Math.max(prev - step, minValue));
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setMonth((prev) => Math.max(prev - step, minValue));
      }, 100);
    }, 200);
  };

  const stopPress = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsCounterProcess(false);
  };

  useEffect(() => {
    if (value <= minValue || value >= maxValue) {
      stopPress();
    }
  }, [value, minValue, maxValue]);

  return (
    <div id="input-month-control" className="input input__number">
      <div style={{ display: 'flex' }}>
        <button
          disabled={value <= minValue}
          onMouseDown={!isMobile ? startPressMinus : undefined}
          onMouseUp={!isMobile ? stopPress : undefined}
          onMouseLeave={!isMobile ? stopPress : undefined}
          onTouchStart={isMobile ? startPressMinus : undefined}
          onTouchEnd={isMobile ? stopPress : undefined}
          className={`ui small icon button${value <= minValue ? ' disabled' : ''}`}
          type="button"
        >
          <i aria-hidden="true" className="minus icon" />
        </button>
        <div className="ui input small">
          <input
            onChange={onChange}
            type="tel"
            onBeforeInput={handleBeforeInput}
            placeholder={placeholder}
            value={value}
            style={{ textAlign: 'right', borderRadius: '0px' }}
          />
        </div>
        <button
          onMouseDown={!isMobile ? startPressPlus : undefined}
          onMouseUp={!isMobile ? stopPress : undefined}
          onMouseLeave={!isMobile ? stopPress : undefined}
          onTouchStart={isMobile ? startPressPlus : undefined}
          onTouchEnd={isMobile ? stopPress : undefined}
          disabled={value >= maxValue}
          className={`ui small icon button${value >= maxValue ? ' disabled' : ''}`}
          type="button"
        >
          <i aria-hidden="true" className="plus icon" />
        </button>
      </div>
    </div>
  );
};

export default FormInputNumber;
